import {
  Button, Modal, ModalBody, ModalFooter, ModalHeader, Spinner,
} from "flowbite-react";
import { Clock, AlertTriangle } from "lucide-react";
import type { WarningLevel } from "@/shared/hooks/useActivityChannel";

type InactivityDialogProps = {
  open: boolean
  message: string
  warningLevel: WarningLevel
  isHandlingActivity: boolean
  countdown: number | null
  onClose: () => void
};

const formatCountdown = (seconds: number) => {
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}:${remainingSeconds.toString().padStart(2, "0")}`;
};

export const InactivityDialog = ({
  open, message, warningLevel, isHandlingActivity, countdown, onClose,
}: InactivityDialogProps) => {
  const isSecondWarning = warningLevel === "second_warning";

  return (
    <Modal show={open} onClose={() => {}} size="md" dismissible={false}>
      <ModalHeader>
        <div className="flex items-center gap-2 text-lg font-semibold">
          {isSecondWarning
            ? <AlertTriangle size={20} className="text-error" />
            : <Clock size={20} className="text-warning" />}
          {isSecondWarning ? "Déconnexion imminente" : "Inactivité détectée"}
        </div>
      </ModalHeader>

      <ModalBody>
        <p className="text-bodytext dark:text-bodytext-emphasis">{message}</p>
        {isSecondWarning && countdown !== null && (
          <div className="mt-4 rounded-lg border border-error p-3 dark:border-error-emphasis dark:bg-error-emphasis/20">
            <div className="flex items-center justify-between">
              <span className="font-medium text-error dark:text-error-emphasis">
                Déconnexion automatique dans :
              </span>
              <span className="text-lg font-bold text-error dark:text-error-emphasis">
                {formatCountdown(countdown)}
              </span>
            </div>
          </div>
        )}
      </ModalBody>

      <ModalFooter className="flex justify-end">
        <Button
          onClick={onClose}
          disabled={isHandlingActivity}
          color={isSecondWarning ? "error" : "primary"}
          className="w-full sm:w-auto"
        >
          {isHandlingActivity
            ? (
              <>
                <Spinner size="sm" className="mr-2" />
                Confirmation...
              </>
            )
            : "Je suis toujours là"}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

InactivityDialog.displayName = "InactivityDialog";
